<template>
  <div class="row" data-cy="date_picker_template">
    <div class="col" data-cy="form_date_datepicker_start">
      <label class="form-label required">{{ $t('datepicker.startdate') }}</label>
      <Datepicker
        v-model="internalStartDate"
        text-input
        :auto-apply="true"
        :time-config="timeConfig"
        :min-date="minDate"
        :formats="datePickerFormats"
        :locale="datepickerLocale"
        :placeholder="$t('datepicker.select_start')"
        :clearable="props.clearable" />
    </div>
    <div class="col-4" data-cy="form_date_datepicker_duration" v-if="!props.hideDuration">
      <label class="form-label" :class="{ required: !props.allowInfinite }">{{ $t('datepicker.days') }}</label>
      <LiFormInput
        v-model.number="numberOfDays"
        :input-class="durationErrors.length ? 'is-invalid' : ''"
        type="number"
        :min="minimumNumberOfDays"
        :clearable="props.allowInfinite"
        :clearableValue="null as any"
        :errors="durationErrors" />
    </div>
    <div class="col-12 text-muted mt-1" data-cy="form_date_datepicker_help">
      <strong class="float-end">{{ formatEndDateForDisplay() }}</strong>
      <small>{{ hintText }}</small>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, watch, ref, onMounted } from 'vue';
import type { WritableComputedRef } from 'vue';
import translator from '@/locale/translator';

import { FormInput as LiFormInput } from '@prospective/lithium';

import { addDays, subSeconds, startOfDay, endOfDay, startOfToday, isToday } from 'date-fns';
import { formatDate, PJAS_DATE_FORMAT } from '@/filters/dateFormat';
import { parseDate as parseUtilDate } from '@/func/dateUtils';
import { VueDatePicker as Datepicker } from '@vuepic/vue-datepicker';
import '@vuepic/vue-datepicker/dist/main.css';
import { getDatepickerLocale } from '@/locale/datepickerLocale';
import type { IProductLimitations } from '@/types/product';

const emit = defineEmits(['update:startdate', 'update:enddate', 'invalidDates']);

interface Props {
  startdate?: string | Date | null;
  enddate?: string | Date | null;
  defaultDuration?: number;
  limitations: IProductLimitations;
  clearable?: boolean;
  hideDuration?: boolean;
  allowInfinite?: boolean;
  helpText?: string;
}

const props = withDefaults(defineProps<Props>(), {
  defaultDuration: 0,
  clearable: false,
  hideDuration: false,
  allowInfinite: false,
});

const timeConfig = { enableTimePicker: false, startTime: { hours: 0, minutes: 0 } };

// Date configuration
const minDate = startOfToday();
const datePickerFormat = computed(() => {
  return translator.get('date.format') || 'yyyy-MM-dd';
});

const datePickerFormats = computed(() => ({
  input: datePickerFormat.value,
}));

const datepickerLocale = computed(() => getDatepickerLocale());

// Helper functions for date conversion
function parseDate(dateValue: string | Date | null | undefined): Date | null {
  return parseUtilDate(dateValue);
}

function emitDate(date: Date | null, type: 'startdate' | 'enddate'): void {
  const formattedDate = date ? formatDate(date, PJAS_DATE_FORMAT) : null;
  emit(`update:${type}`, formattedDate);
}

const hintText = computed(() => {
  if (props.helpText) return props.helpText;
  if (props.hideDuration) return translator.get('datepicker.date_help.only_start');
  return translator.get('datepicker.date_help');
});

// Internal date state management
const internalStartDate: WritableComputedRef<Date> = computed({
  get(): Date {
    const parsed = parseDate(props.startdate);
    // If a valid start date is provided, use it and normalize to start of day
    if (parsed) {
      return startOfDay(parsed);
    }
    // If no valid start date provided, default to today (start of day)
    return minDate;
  },
  set(value: Date): void {
    emitDate(value, 'startdate');
  },
});

const internalEndDate = computed((): Date | null => {
  if (props.allowInfinite && !props.enddate) return null;
  const parsed = parseDate(props.enddate);
  // If a valid end date is provided, use it and normalize to end of day
  if (parsed) {
    return endOfDay(parsed);
  }
  // If no valid end date and we have a start date, calculate from start + duration, but subtract 1 second to ensure it ends at the end of the last day
  if (numberOfDays.value > 0) {
    // If the start date is today, we just add the number of days
    if (isToday(internalStartDate.value)) {
      return addDays(internalStartDate.value, numberOfDays.value);
    }
    // Otherwise, we subtract 1 second to ensure it ends at the end of the last day
    return subSeconds(addDays(internalStartDate.value, numberOfDays.value), 1);
  }
  return null;
});

// Duration management
const numberOfDays = ref<number>(props.defaultDuration);

const minimumNumberOfDays = computed(() => props.defaultDuration || props.limitations.duration_min || 1);
const maximumNumberOfDays = computed(() => props.limitations.duration_max || Infinity);

const durationErrors = computed(() => {
  if (props.allowInfinite && !numberOfDays.value) return [];
  if (numberOfDays.value < minimumNumberOfDays.value) {
    return [`The minimum duration is ${minimumNumberOfDays.value} days`];
  }
  if (numberOfDays.value > maximumNumberOfDays.value) {
    return [`The maximum duration is ${maximumNumberOfDays.value} days`];
  }
  return [];
});

// Display formatting
function formatEndDateForDisplay(): string {
  const endDate = calculateEndDate();
  if (!endDate) return translator.get('datepicker.infinite_duration');
  return formatDate(endDate, datePickerFormat.value);
}

function calculateEndDate(): Date | null {
  if (props.allowInfinite && !numberOfDays.value) return null;
  // Calculate end date based on start date and duration, but subtract 1 second to ensure it ends at the end of the last day
  if (isToday(internalStartDate.value)) {
    return addDays(internalStartDate.value, numberOfDays.value);
  }
  return subSeconds(addDays(internalStartDate.value, numberOfDays.value), 1);
}

function calculateHumanDateDiff(date1: Date, date2: Date): number {
  const millisecondsPerDay = 1000 * 60 * 60 * 24;
  const differenceInMilliseconds = Math.abs(date1.getTime() - date2.getTime());

  return Math.round(differenceInMilliseconds / millisecondsPerDay);
}

// Initialization and synchronization
function initializeDuration(): void {
  if (internalEndDate.value) {
    // Calculate duration from existing dates
    numberOfDays.value = calculateHumanDateDiff(internalEndDate.value, internalStartDate.value);
  } else {
    // Set minimum duration if not allowing infinite
    numberOfDays.value = props.allowInfinite ? 0 : minimumNumberOfDays.value;
  }
}

function updateEndDate(): void {
  const endDate = calculateEndDate();
  emitDate(endDate, 'enddate');
}

// Watchers
watch(numberOfDays, () => {
  updateEndDate();
});

watch(
  () => props.defaultDuration,
  (newDuration) => {
    if (newDuration > 0) {
      numberOfDays.value = newDuration;
    }
  }
);

watch(internalStartDate, () => {
  updateEndDate();
});

watch(minimumNumberOfDays, (newMinimum) => {
  if (!props.allowInfinite && numberOfDays.value < newMinimum) {
    numberOfDays.value = newMinimum;
  }
});

watch(
  durationErrors,
  () => {
    emit('invalidDates', durationErrors.value.length > 0);
  },
  { immediate: true }
);

// Initialize on mount
onMounted(() => {
  // Emit default values if props are undefined/null
  if (!parseDate(props.startdate)) {
    emitDate(minDate, 'startdate');
  }

  initializeDuration();
  updateEndDate();
});
</script>
